import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faUnlock,
  faKey,
  faEye,
  faEyeSlash,
  faSpinner,
  faTimes,
  faShieldAlt,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { itemLockAPI } from '../services/api';
import useI18n from '../hooks/useI18n';
import '../styles/PasswordLockModal.css';

interface PasswordLockModalProps {
  itemId: string;
  itemName: string;
  isLocked: boolean;
  mode: 'lock' | 'unlock' | 'verify' | 'change-password' | 'remove-lock';
  onClose: () => void;
  onSuccess: (isLocked: boolean) => void;
}

const PasswordLockModal: React.FC<PasswordLockModalProps> = ({
  itemId,
  itemName,
  isLocked,
  mode,
  onClose,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useI18n();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'lock') {
      if (password.length < 4) {
        setError(t('lockModal.minCharsError'));
        return;
      }
      if (password !== confirmPassword) {
        setError(t('lockModal.mismatchError'));
        return;
      }
    }

    if (mode === 'change-password') {
      if (newPassword.length < 4) {
        setError(t('lockModal.newMinCharsError'));
        return;
      }
      if (newPassword !== confirmPassword) {
        setError(t('lockModal.newMismatchError'));
        return;
      }
    }

    setLoading(true);
    try {
      switch (mode) {
        case 'lock':
          await itemLockAPI.lock(itemId, password);
          onSuccess(true);
          break;
        case 'unlock':
          await itemLockAPI.unlock(itemId, password);
          onSuccess(false);
          break;
        case 'verify':
          await itemLockAPI.verifyLock(itemId, password);
          onSuccess(isLocked);
          break;
        case 'change-password':
          await itemLockAPI.changeLockPassword(itemId, password, newPassword);
          onSuccess(true);
          break;
        case 'remove-lock':
          await itemLockAPI.removeLock(itemId);
          onSuccess(false);
          break;
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || t('lockModal.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'lock': return t('lockModal.lockItem');
      case 'unlock': return t('lockModal.unlockItem');
      case 'verify': return t('lockModal.verifyPassword');
      case 'change-password': return t('lockModal.changeLockPassword');
      case 'remove-lock': return t('lockModal.removeLock') || 'Remove Lock';
    }
  };

  const getIcon = () => {
    switch (mode) {
      case 'lock': return faLock;
      case 'unlock': return faUnlock;
      case 'verify': return faShieldAlt;
      case 'change-password': return faKey;
      case 'remove-lock': return faUnlock;
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'lock': return t('lockModal.lockDesc');
      case 'unlock': return t('lockModal.unlockDesc');
      case 'verify': return t('lockModal.verifyDesc');
      case 'change-password': return t('lockModal.changeDesc');
      case 'remove-lock': return t('lockModal.removeLockDesc') || 'This will remove the lock from this item. No password is required.';
    }
  };

  return (
    <div className="plm-overlay" onClick={onClose}>
      <div className="plm-modal" onClick={e => e.stopPropagation()}>
        <button className="plm-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="plm-header">
          <div className="plm-icon" data-mode={mode}>
            <FontAwesomeIcon icon={getIcon()} />
          </div>
          <h2>{getTitle()}</h2>
          <p className="plm-item-name">{itemName}</p>
          <p className="plm-desc">{getDescription()}</p>
        </div>

        {error && (
          <div className="plm-error">
            <FontAwesomeIcon icon={faExclamationCircle} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="plm-form">
          {/* Password field (all modes except remove-lock) */}
          {mode !== 'remove-lock' && (
          <div className="plm-field">
            <label>{mode === 'change-password' ? t('lockModal.currentPassword') : t('lockModal.password')}</label>
            <div className="plm-input-wrapper">
              <FontAwesomeIcon icon={faLock} className="plm-input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                placeholder={mode === 'lock' ? t('lockModal.choosePlaceholder') : t('lockModal.enterPlaceholder')}
                autoFocus
                disabled={loading}
              />
              <button
                type="button"
                className="plm-toggle-vis"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>
          )}

          {/* New password field (lock & change-password modes) */}
          {mode === 'change-password' && (
            <div className="plm-field">
              <label>{t('lockModal.newPassword')}</label>
              <div className="plm-input-wrapper">
                <FontAwesomeIcon icon={faKey} className="plm-input-icon" />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setError(''); }}
                  placeholder={t('lockModal.newPlaceholder')}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="plm-toggle-vis"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>
          )}

          {/* Confirm password (lock & change-password modes) */}
          {(mode === 'lock' || mode === 'change-password') && (
            <div className="plm-field">
            <label>{mode === 'change-password' ? t('lockModal.confirmNewPassword') : t('lockModal.confirmPassword')}</label>
            <div className="plm-input-wrapper">
              <FontAwesomeIcon icon={faLock} className="plm-input-icon" />
              <input
                type="password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder={t('lockModal.reenterPlaceholder')}
                  disabled={loading}
                />
              </div>
            </div>
          )}

          <button type="submit" className="plm-submit" disabled={loading} data-mode={mode}>
            {loading ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> {t('lockModal.processing')}</>
            ) : (
              <>
                <FontAwesomeIcon icon={getIcon()} />
                {mode === 'lock' ? t('lockModal.lockItem') : mode === 'unlock' ? t('lockModal.unlockItem') : mode === 'verify' ? t('lockModal.verify') : mode === 'remove-lock' ? (t('lockModal.removeLock') || 'Remove Lock') : t('lockModal.changePassword')}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordLockModal;
